import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAppLogo } from '../hooks/useAppLogo';
import { companyAPI } from '../services/api';
import Icon from './Icon';
import { DEFAULT_APP_LOGO } from '../constants';
import './Sidebar.css';

const APP_LOGO = DEFAULT_APP_LOGO;

function Sidebar({ user, onLogout, darkMode = true, onToggleDarkMode, subscriptionTier }) {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  /** 'drawer' = hamburger + off-canvas (phone & touch tablets); 'hover' = auto-hide, cursor reveals (mouse desktop) */
  const [navMode, setNavMode] = useState(() => {
    if (typeof window === 'undefined') return 'drawer';
    // Phone + tablet + iPad landscape: hamburger drawer (hover rail only on wide desktop)
    return window.innerWidth <= 1366 ? 'drawer' : 'hover';
  });
  const [companyLogo, setCompanyLogo] = useState(null);
  const [companyName, setCompanyName] = useState(null);
  const [companySubscriptionTier, setCompanySubscriptionTier] = useState(null);
  const [technicianCanAddBikes, setTechnicianCanAddBikes] = useState(false);
  const [technicianCanAddServices, setTechnicianCanAddServices] = useState(false);
  const theme = darkMode ? 'night' : 'day';
  const { logoUrl: appLogoUrl, faviconDay, faviconNight } = useAppLogo(theme);
  const faviconUrl = darkMode ? faviconNight : faviconDay;
  const logoUrl = APP_LOGO || appLogoUrl;
  
  // Fetch company logo for company admins and technicians
  useEffect(() => {
    const fetchCompanyInfo = async () => {
      if (user?.role === 'company_admin' || user?.role === 'admin' || user?.role === 'technician') {
        try {
          const response = await companyAPI.getCompanyInfo();
          const company = response.data?.company;
          setCompanyLogo(company?.logo_url || DEFAULT_APP_LOGO);
          if (company?.name) setCompanyName(company.name);
          if (company?.subscription_tier) setCompanySubscriptionTier(company.subscription_tier);
          if (user?.role === 'technician') {
            setTechnicianCanAddBikes(company?.technician_can_add_bikes === true);
            setTechnicianCanAddServices(company?.technician_can_add_services === true);
          }
        } catch (err) {
          // Silently fail - company logo is optional
          console.log('Could not fetch company logo');
        }
      }
    };
    fetchCompanyInfo();
  }, [user?.role, user?.company_id]);

  const effectiveTier = (subscriptionTier || companySubscriptionTier || '').toLowerCase();
  const isEnterpriseOrLifetime = ['enterprise', 'lifetime'].includes(effectiveTier);
  const isLockedPath = (path) => {
    if (isEnterpriseOrLifetime) return false;
    if (path === '/calendar') return true;
    if (path === '/admin/management?tab=inventory') return true;
    if (path === '/admin/company-settings?tab=marketing') return true;
    return false;
  };
  
  // Check if demo mode is enabled (runtime flag or build-time env)
  const isDemo = Boolean(
    (typeof window !== 'undefined' && window.__DEMO_MODE__) ||
    (typeof process !== 'undefined' && process.env && process.env.REACT_APP_DEMO_MODE === 'true')
  );

  const computeNavMode = () => {
    if (typeof window === 'undefined') return 'drawer';
    return window.innerWidth <= 1366 ? 'drawer' : 'hover';
  };

  useEffect(() => {
    const syncNavMode = () => {
      const next = computeNavMode();
      setNavMode(next);
      if (next === 'hover') {
        setIsMobileOpen(false);
      }
    };

    syncNavMode();
    window.addEventListener('resize', syncNavMode);
    return () => {
      window.removeEventListener('resize', syncNavMode);
    };
  }, []);

  useEffect(() => {
    if (navMode === 'hover') {
      setCollapsed(false);
    }
  }, [navMode]);

  // Close drawer when route changes (including query params)
  useEffect(() => {
    if (navMode === 'drawer') {
      setIsMobileOpen(false);
    }
  }, [location.pathname, location.search, navMode]);

  const isDrawerNav = navMode === 'drawer';
  const isHoverReveal = navMode === 'hover';

  const isActive = (path) => {
    // Handle paths with query params (e.g., /dashboard?tab=calendar)
    if (path.includes('?')) {
      const [pathname, queryString] = path.split('?');
      const params = new URLSearchParams(queryString);
      const currentParams = new URLSearchParams(location.search);
      
      if (location.pathname !== pathname) return false;
      
      // Check if all params in the menu path match current URL params
      for (const [key, value] of params.entries()) {
        if (currentParams.get(key) !== value) return false;
      }
      return true;
    }
    
    if (path === '/dashboard') {
      // Dashboard without query params - only active if no tab param
      const currentParams = new URLSearchParams(location.search);
      return (location.pathname === '/dashboard' || location.pathname === '/') && !currentParams.get('tab');
    }
    return location.pathname.startsWith(path);
  };

  // Simplified menu structure
  const getMenuItemsForRole = () => {
    const role = user?.role;
    
    if (role === 'super_admin') {
      return [
        { path: '/dashboard', iconName: 'home', label: 'Dashboard', section: 'main' },
        { path: '/admin', iconName: 'admin', label: 'Admin Panel', section: 'admin' },
        { path: '/admin/company-settings', iconName: 'settings', label: 'Settings', section: 'company' },
        { path: '/profile', iconName: 'profile', label: 'Profile', section: 'account' },
        { path: '/billing', iconName: 'billing', label: 'Billing', section: 'account' },
        { path: '/help', iconName: 'help', label: 'Help', section: 'account' },
      ];
    }
    
    if (role === 'company_admin' || role === 'admin') {
      const items = [
        { path: '/dashboard', iconName: 'home', label: 'Dashboard', section: 'main' },
        { path: '/calendar', iconName: 'calendar', label: 'Calendar', section: 'main' },
        { path: '/admin/management?tab=bikes', iconName: 'bike', label: 'Bikes', section: 'main' },
        { path: '/admin/management?tab=services', iconName: 'service', label: 'Services', section: 'main' },
        { path: '/admin/management?tab=inventory', iconName: 'inventory', label: 'Inventory', section: 'main' },
        { path: '/admin/management?tab=users', iconName: 'profile', label: 'Users', section: 'main' },
        { path: '/admin/company-settings?tab=marketing', iconName: 'marketing', label: 'Marketing', section: 'main' },
        { path: '/company/achievements', iconName: 'achievements', label: 'Achievements', section: 'main' },
        { path: '/admin/company-settings', iconName: 'settings', label: 'Settings', section: 'company' },
        { path: '/profile', iconName: 'profile', label: 'Profile', section: 'account' },
        { path: '/billing', iconName: 'billing', label: 'Billing', section: 'account' },
        { path: '/help', iconName: 'help', label: 'Help', section: 'account' },
        { path: '/support', iconName: 'support', label: 'Support', section: 'account' },
      ];
      return items;
    }
    
    if (role === 'technician') {
      const items = [
        { path: '/dashboard', iconName: 'home', label: 'Dashboard', section: 'main' },
        { path: '/dashboard?tab=calendar', iconName: 'calendar', label: 'Calendar', section: 'main' },
        { path: '/dashboard?tab=timeoff', iconName: 'timeOff', label: 'Time Off', section: 'main' },
      ];
      if (technicianCanAddBikes || technicianCanAddServices) {
        if (technicianCanAddBikes) items.push({ path: '/admin/management?tab=bikes', iconName: 'bike', label: 'Bikes', section: 'main' });
        if (technicianCanAddServices) items.push({ path: '/admin/management?tab=services', iconName: 'service', label: 'Services', section: 'main' });
      }
      items.push(
        { path: '/profile', iconName: 'profile', label: 'Profile', section: 'account' },
        { path: '/help', iconName: 'help', label: 'Help', section: 'account' },
        { path: '/support', iconName: 'support', label: 'Support', section: 'account' },
      );
      return items;
    }
    
    // Default user role
    return [
      { path: '/dashboard', iconName: 'home', label: 'Home', section: 'main' },
      { path: '/my-bikes', iconName: 'bikePassports', label: 'My Bike Passports', section: 'main' },
      { path: '/companies', iconName: 'shops', label: 'Service Shops', section: 'main' },
      { path: '/book', iconName: 'calendar', label: 'Book Service', section: 'main' },
      { path: '/profile', iconName: 'profile', label: 'Profile', section: 'account' },
      { path: '/help', iconName: 'help', label: 'Help', section: 'account' },
      { path: '/support', iconName: 'support', label: 'Support', section: 'account' },
    ];
  };

  const menuItems = getMenuItemsForRole();
  const mainItems = menuItems.filter(item => item.section === 'main');
  const adminItems = menuItems.filter(item => item.section === 'admin');
  const companyItems = menuItems.filter(item => item.section === 'company');
  const accountItems = menuItems.filter(item => item.section === 'account');

  const getRoleLabel = (role) => {
    switch (role) {
      case 'super_admin': return 'Super Admin';
      case 'company_admin': return 'Company Admin';
      case 'admin': return 'Admin';
      case 'technician': return 'Tech';
      default: return 'User';
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'super_admin': return '#ff6b6b';
      case 'company_admin': return '#4ecdc4';
      case 'admin': return '#667eea';
      case 'technician': return '#667eea';
      default: return '#3d5a20';
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileOpen(!isMobileOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileOpen(false);
  };

  const slotClass = [
    'sidebar-slot',
    isDrawerNav ? 'sidebar-slot--drawer' : '',
    isHoverReveal ? 'sidebar-slot--hover-reveal' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={slotClass}>
      {/* Drawer: overlay + top bar (phones & touch iPads / tablets) */}
      {isDrawerNav && isMobileOpen && (
        <div className="sidebar-overlay" onClick={closeMobileMenu}></div>
      )}

      {isDrawerNav && (
        <div
          className="mobile-top-bar"
          role="button"
          tabIndex={0}
          onClick={toggleMobileMenu}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggleMobileMenu();
            }
          }}
          aria-label="Toggle navigation menu"
        >
          <button
            type="button"
            className="mobile-menu-toggle"
            onClick={(e) => {
              e.stopPropagation();
              toggleMobileMenu();
            }}
            aria-label="Toggle navigation menu"
            aria-expanded={isMobileOpen}
          >
            <div className={`hamburger-icon ${isMobileOpen ? 'open' : ''}`}>
              <span></span>
              <span></span>
              <span></span>
            </div>
          </button>
          <span className="mobile-top-bar-label" aria-hidden="true">
            Menu
          </span>
        </div>
      )}

      <aside
        className={`sidebar ${collapsed && !isHoverReveal ? 'collapsed' : ''} ${darkMode ? 'dark' : 'light'} ${isMobileOpen ? 'mobile-open' : ''} ${isHoverReveal ? 'sidebar--hover-reveal-panel' : ''}`}
      >
      {/* Logo Section */}
      <div className="sidebar-header">
        <Link to="/dashboard" className="logo-wrapper" onClick={closeMobileMenu}>
          <div className="logo-icon">
            {(collapsed ? (faviconUrl || logoUrl) : logoUrl) && (
              <img 
                src={collapsed ? (faviconUrl || logoUrl) : logoUrl} 
                alt="Frame-Book logo" 
                className="logo-image" 
              />
            )}
          </div>
        </Link>
        {!isHoverReveal && (
          <button
            type="button"
            className="collapse-btn"
            onClick={() => setCollapsed(!collapsed)}
            title={collapsed ? 'Expand' : 'Collapse'}
          >
            {collapsed ? '→' : '←'}
          </button>
        )}
      </div>
      
      {/* Company Logo Section - for company admins and technicians */}
      {companyLogo && (user?.role === 'company_admin' || user?.role === 'admin' || user?.role === 'technician') && (
        <div className="company-logo-section" style={{
          padding: collapsed ? '8px' : '12px 16px',
          borderBottom: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.08)',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          justifyContent: collapsed ? 'center' : 'flex-start'
        }}>
          <img 
            src={companyLogo} 
            alt={companyName || 'Company logo'} 
            style={{
              width: collapsed ? '32px' : '40px',
              height: collapsed ? '32px' : '40px',
              objectFit: 'contain',
              borderRadius: '8px',
              background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
              padding: '4px'
            }}
          />
          {!collapsed && companyName && (
            <span style={{
              fontSize: '13px',
              fontWeight: '600',
              color: darkMode ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1
            }}>
              {companyName}
            </span>
          )}
        </div>
      )}

      {/* Navigation */}
      <nav className="sidebar-nav">
        {/* Main Menu */}
        {mainItems.length > 0 && (
          <div className="nav-section">
            {!collapsed && <span className="nav-section-title">Main</span>}
            {mainItems.map((item, idx) => (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
                onMouseEnter={() => setHoveredItem(item.path)}
                onMouseLeave={() => setHoveredItem(null)}
                style={{ animationDelay: `${idx * 0.05}s` }}
              >
                <span className="nav-icon">
                  <Icon 
                    name={item.iconName} 
                    color={isActive(item.path) ? 'nav_active' : 'nav_inactive'} 
                    darkMode={darkMode}
                    size={20}
                  />
                </span>
                {!collapsed && (
                  <>
                    <span className="nav-label">{item.label}</span>
                    {isLockedPath(item.path) && (
                      <Icon name="lock" size={12} color="muted" darkMode={darkMode} style={{ marginLeft: '4px', opacity: 0.7 }} />
                    )}
                  </>
                )}
                {isActive(item.path) && <div className="active-indicator"></div>}
                {collapsed && hoveredItem === item.path && (
                  <div className="tooltip">{item.label}{isLockedPath(item.path) ? ' (Enterprise)' : ''}</div>
                )}
              </Link>
            ))}
          </div>
        )}

        {/* Admin Menu */}
        {adminItems.length > 0 && (
          <div className="nav-section">
            {!collapsed && <span className="nav-section-title">Administration</span>}
            {adminItems.map((item, idx) => (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
                onMouseEnter={() => setHoveredItem(item.path)}
                onMouseLeave={() => setHoveredItem(null)}
                style={{ animationDelay: `${(idx + mainItems.length) * 0.05}s` }}
              >
                <span className="nav-icon">
                  <Icon 
                    name={item.iconName} 
                    color={isActive(item.path) ? 'nav_active' : 'nav_inactive'} 
                    darkMode={darkMode}
                    size={20}
                  />
                </span>
                {!collapsed && <span className="nav-label">{item.label}</span>}
                {isActive(item.path) && <div className="active-indicator"></div>}
                {collapsed && hoveredItem === item.path && (
                  <div className="tooltip">{item.label}</div>
                )}
              </Link>
            ))}
          </div>
        )}

        {/* Company Menu */}
        {companyItems.length > 0 && (
          <div className="nav-section">
            {!collapsed && <span className="nav-section-title">Company</span>}
            {companyItems.map((item, idx) => (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
                onMouseEnter={() => setHoveredItem(item.path)}
                onMouseLeave={() => setHoveredItem(null)}
                style={{ animationDelay: `${(idx + mainItems.length + adminItems.length) * 0.05}s` }}
              >
                <span className="nav-icon">
                  <Icon 
                    name={item.iconName} 
                    color={isActive(item.path) ? 'nav_active' : 'nav_inactive'} 
                    darkMode={darkMode}
                    size={20}
                  />
                </span>
                {!collapsed && <span className="nav-label">{item.label}</span>}
                {isActive(item.path) && <div className="active-indicator"></div>}
                {collapsed && hoveredItem === item.path && (
                  <div className="tooltip">{item.label}</div>
                )}
              </Link>
            ))}
          </div>
        )}

        {/* Account Menu */}
        {accountItems.length > 0 && (
          <div className="nav-section">
            {!collapsed && <span className="nav-section-title">Account</span>}
            {accountItems.map((item, idx) => (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
                onMouseEnter={() => setHoveredItem(item.path)}
                onMouseLeave={() => setHoveredItem(null)}
                style={{ animationDelay: `${(idx + mainItems.length + adminItems.length + companyItems.length) * 0.05}s` }}
              >
                <span className="nav-icon">
                  <Icon 
                    name={item.iconName} 
                    color={isActive(item.path) ? 'nav_active' : 'nav_inactive'} 
                    darkMode={darkMode}
                    size={20}
                  />
                </span>
                {!collapsed && <span className="nav-label">{item.label}</span>}
                {isActive(item.path) && <div className="active-indicator"></div>}
                {collapsed && hoveredItem === item.path && (
                  <div className="tooltip">{item.label}</div>
                )}
              </Link>
            ))}
          </div>
        )}
      </nav>

      {/* Bottom Section */}
      <div className="sidebar-footer">
        {/* User Profile Card (now anchored above theme toggle) */}
        <div className="user-card footer-user-card">
          <div className="user-avatar" style={{ borderColor: getRoleColor(user?.role) }}>
            {user?.profile_image_url ? (
              <img src={user.profile_image_url} alt="" aria-hidden />
            ) : (
              <span>{user?.firstname?.[0] || user?.username?.[0] || '?'}</span>
            )}
            <div className="status-dot"></div>
          </div>
          {!collapsed && (
            <div className="user-info">
              <span className="user-name">{user?.firstname || user?.surname ? [user?.firstname, user?.surname].filter(Boolean).join(' ') : user?.username}</span>
              <span className="user-role" style={{ color: getRoleColor(user?.role) }}>
                {getRoleLabel(user?.role)}
              </span>
            </div>
          )}
        </div>

        {/* Theme Toggle Button */}
        {onToggleDarkMode && (
          <button 
            className="theme-btn"
            onClick={onToggleDarkMode}
            onMouseEnter={() => setHoveredItem('theme')}
            onMouseLeave={() => setHoveredItem(null)}
          >
            <span className="nav-icon">
              <Icon 
                name={darkMode ? 'lightMode' : 'darkMode'} 
                color="nav_inactive" 
                darkMode={darkMode}
                size={20}
              />
            </span>
            {!collapsed && (
              <span className="nav-label">
                {darkMode ? 'Light mode' : 'Dark mode'}
              </span>
            )}
            {collapsed && hoveredItem === 'theme' && (
              <div className="tooltip">
                {darkMode ? 'Light mode' : 'Dark mode'}
              </div>
            )}
          </button>
        )}
        {/* Back to Landing Button (Demo Mode Only) */}
        {isDemo && (
          <Link
            to="/"
            className="back-to-landing-btn"
            onMouseEnter={() => setHoveredItem('landing')}
            onMouseLeave={() => setHoveredItem(null)}
          >
            <span className="nav-icon">
              <Icon name="home" color="nav_inactive" darkMode={darkMode} size={20} />
            </span>
            {!collapsed && <span className="nav-label">Back to Landing</span>}
            {collapsed && hoveredItem === 'landing' && (
              <div className="tooltip">Back to Landing</div>
            )}
          </Link>
        )}
        <button 
          className="logout-btn"
          onClick={onLogout}
          onMouseEnter={() => setHoveredItem('logout')}
          onMouseLeave={() => setHoveredItem(null)}
        >
          <span className="nav-icon">
            <Icon name="logout" color="nav_inactive" darkMode={darkMode} size={20} />
          </span>
          {!collapsed && <span className="nav-label">Logout</span>}
          {collapsed && hoveredItem === 'logout' && (
            <div className="tooltip">Logout</div>
          )}
        </button>
      </div>
    </aside>
    </div>
  );
}

export default Sidebar;
